import { Command } from './Command';
import { updateOverlapStates } from '../components/Editor/utils/overlapDetection';
import { GARDEN_OBJECT_TYPES } from '../constants/gardenObjectTypes';
import { textureManager } from '../lib/textures/textureManager';
import { supportsTexture } from '../lib/textures/materialMappings';

/**
 * CreateObjectCommand - Creates a new object on the canvas
 * Used for: Rectangle, Circle, Line, Polygon, Bezier, etc.
 *
 * @example
 * const rect = new Rect({ width: 100, height: 50, fill: 'blue' });
 * const command = new CreateObjectCommand(canvas, rect);
 * commandStack.execute(command);
 */
export class CreateObjectCommand extends Command {
  /**
   * @param {Canvas} canvas - Fabric.js canvas instance
   * @param {Object} fabricObject - Fabric.js object to create (Rect, Circle, Line, etc.)
   */
  constructor(canvas, fabricObject) {
    super();
    this.canvas = canvas;
    this.object = fabricObject;
  }

  execute() {
    this.canvas.add(this.object);
    this.canvas.setActiveObject(this.object);
    this.canvas.renderAll();

    // Apply default texture for garden objects that support it
    const objType = this.object.data?.objectType || this.object.data?.gardenObjectType;
    if (objType && supportsTexture(objType) && !this.object.data?.useCustomColor) {
      textureManager.applyTexture(this.object, this.canvas);
    }

    // Update overlap detection if this is a plant
    if (this.object.objectType === GARDEN_OBJECT_TYPES.PLANT) {
      updateOverlapStates(this.canvas);
    }
  }

  undo() {
    this.canvas.remove(this.object);
    this.canvas.discardActiveObject();
    this.canvas.renderAll();

    // Update overlap detection after removing plant
    if (this.object.objectType === GARDEN_OBJECT_TYPES.PLANT) {
      updateOverlapStates(this.canvas);
    }
  }

  getDescription() {
    const type = this.object.data?.type || this.object.type;
    return `Create ${type}`;
  }
}
